import React from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Box,
  Text,
  VStack,
  HStack,
  Progress,
} from "@chakra-ui/react";
import { IBucket } from "../../views/Orders/store/useBucketsStore";
import { calculateRemainingParts } from "./utils/partsCalculationUtils";
import { PartsBreakdownPanel } from "./PartsBreakdownPanel";
import SupplierBucketList from "./SupplierBucketList";
import { BucketType } from "./types";

interface SavedBucketDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  bucket: IBucket | null;
}

const SavedBucketDetailsModal: React.FC<SavedBucketDetailsModalProps> = ({
  isOpen,
  onClose,
  bucket,
}) => {
  const calculationForBucket = React.useMemo(() => {
    if (!bucket) return null;
    return calculateRemainingParts(bucket.originalParts, bucket.suppliers);
  }, [bucket]);

  const isCurrentBucket = bucket?.id === BucketType.IN_PROGRESS;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" isCentered>
      <ModalOverlay />
      <ModalContent maxH="90vh">
        <ModalHeader fontFamily="'Nunito','Inter', sans-serif">
          {isCurrentBucket
            ? "Current Bucket Details"
            : "Supplier Bucket Details"}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody overflowY="auto" maxH="85vh" pb={6} px={6}>
          {bucket && calculationForBucket && (
            <VStack spacing={5} align="stretch">
              <Box bg="white" borderRadius="xl" p={2} boxShadow="md">
                <Text
                  fontSize="1rem"
                  fontWeight="bold"
                  color="gray.800"
                  mb={2}
                  fontFamily="'Nunito','Inter', sans-serif"
                >
                  Order Progress
                </Text>
                <HStack justify="space-between" align="center" mb={2}>
                  <Text
                    fontSize="1.25rem"
                    fontWeight={900}
                    color="#3B82F6"
                    fontFamily="'Nunito','Inter', sans-serif"
                  >
                    {calculationForBucket.fulfillmentPercentage.toFixed(0)}%
                  </Text>
                  <Text
                    fontSize="0.8rem"
                    color="gray.600"
                    fontFamily="'Nunito','Inter', sans-serif"
                    fontWeight={700}
                  >
                    {calculationForBucket.isFullyFulfilled
                      ? "Complete"
                      : "In Progress"}
                  </Text>
                </HStack>
                <Progress
                  value={calculationForBucket.fulfillmentPercentage}
                  size="md"
                  borderRadius="full"
                  bg="gray.200"
                  transition="all 0.3s ease"
                  sx={{
                    "& > div": {
                      background:
                        "linear-gradient(90deg,rgb(74, 141, 250),rgb(30, 195, 140))",
                      borderRadius: "full",
                      transition: "width 0.3s ease",
                    },
                  }}
                />
              </Box>

              <PartsBreakdownPanel
                selectedSuppliers={bucket.suppliers}
                remainingParts={calculationForBucket.remainingParts}
                originalParts={bucket.originalParts}
                reverseOrder={true}
              />

              <Box
                bg="white"
                borderRadius="xl"
                p={5}
                boxShadow="md"
                border="1px solid"
                borderColor="gray.200"
              >
                <Text
                  fontSize="1.1rem"
                  fontWeight="bold"
                  color="gray.800"
                  mb={5}
                  fontFamily="'Nunito','Inter', sans-serif"
                >
                  Suppliers ({bucket.suppliers.length})
                </Text>
                <Box
                  maxH="50vh"
                  overflowY="auto"
                  pr={2}
                  css={{
                    "&::-webkit-scrollbar": {
                      width: "8px",
                    },
                    "&::-webkit-scrollbar-track": {
                      background: "#f1f1f1",
                      borderRadius: "4px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      background: "#c1c1c1",
                      borderRadius: "4px",
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                      background: "#a8a8a8",
                    },
                  }}
                >
                  <SupplierBucketList
                    selectedSuppliers={bucket.suppliers}
                    showOrderBadge={true}
                    originalParts={bucket.originalParts}
                  />
                </Box>
              </Box>
            </VStack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default SavedBucketDetailsModal;
